import { useState } from 'react';
import { Button } from "@tonic/shared/components/ui";
import { Card, CardContent } from "@tonic/shared/components/ui";
import { Upload, X, Star, GripVertical } from 'lucide-react';
import { useUploadProductImage, useDeleteProductImage, useUpdateProductImages } from '../../../hooks/useProductImages';
import PropTypes from 'prop-types';

export function ImagesTab({ product }) {
  const [images, setImages] = useState(product.images);
  const uploadMutation = useUploadProductImage(product.id);
  const deleteMutation = useDeleteProductImage(product.id);
  const updateMutation = useUpdateProductImages(product.id);

  const handleImageUpload = async (e) => {
    const files = e.target.files;
    if (!files) return;

    for (const file of Array.from(files)) {
      try {
        await uploadMutation.mutateAsync(file);
      } catch (error) {
        console.error('Failed to upload image:', error);
        // TODO: Show error toast
      }
    }
  };

  const handleRemoveImage = async (imageId) => {
    try {
      await deleteMutation.mutateAsync(imageId);
    } catch (error) {
      console.error('Failed to delete image:', error);
      // TODO: Show error toast
    }
  };

  const handleSetPrimary = async (imageId) => {
    const updatedImages = images.map(img => ({
      id: img.id,
      sortOrder: img.sortOrder,
      isPrimary: img.id === imageId
    }));

    try {
      await updateMutation.mutateAsync(updatedImages);
    } catch (error) {
      console.error('Failed to update images:', error);
      // TODO: Show error toast
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-4 gap-4">
            {images.map((image) => (
              <div 
                key={image.id} 
                className="relative group border rounded-lg overflow-hidden"
              >
                <img 
                  src={image.url} 
                  alt="Product" 
                  className="w-full aspect-square object-cover"
                />
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="absolute top-2 right-2 flex space-x-1">
                    <Button 
                      variant="ghost" 
                      size="sm"
                      className="h-8 w-8 p-0 text-white hover:text-white hover:bg-white/20"
                      onClick={() => handleSetPrimary(image.id)}
                      disabled={uploadMutation.isPending || deleteMutation.isPending || updateMutation.isPending}
                    >
                      <Star 
                        className={`h-4 w-4 ${image.isPrimary ? 'fill-white' : ''}`} 
                      />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      className="h-8 w-8 p-0 text-white hover:text-white hover:bg-white/20"
                      onClick={() => handleRemoveImage(image.id)}
                      disabled={uploadMutation.isPending || deleteMutation.isPending || updateMutation.isPending}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    className="absolute bottom-2 left-2 h-8 w-8 p-0 text-white hover:text-white hover:bg-white/20 cursor-move"
                    disabled={uploadMutation.isPending || deleteMutation.isPending || updateMutation.isPending}
                  >
                    <GripVertical className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}

            <label className={`border-2 border-dashed rounded-lg aspect-square flex flex-col items-center justify-center cursor-pointer hover:bg-gray-50 transition-colors
              ${uploadMutation.isPending ? 'opacity-50 cursor-not-allowed' : ''}`}>
              <div className="flex flex-col items-center justify-center p-6">
                <Upload className="h-8 w-8 mb-2 text-gray-400" />
                <span className="text-sm text-gray-600">Upload images</span>
              </div>
              <input
                type="file"
                multiple
                accept="image/*"
                className="hidden"
                onChange={handleImageUpload}
                disabled={uploadMutation.isPending}
              />
            </label>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

ImagesTab.propTypes = {
  product: PropTypes.shape({
    id: PropTypes.string.isRequired,
    images: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired,
      sortOrder: PropTypes.number.isRequired,
      isPrimary: PropTypes.bool.isRequired,
    })).isRequired,
  }).isRequired,
};